import React, { useState, useEffect } from "react";
import { 
  Box, 
  Button, 
  Stack,
  Typography,
  Avatar
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import ThreadInput from "./ThreadInput";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useUser } from "../../contexts/UserContext";

const ThreadComposer = ({ 
  threads = [{ id: 1, text: "", isMain: true }],
  onThreadsChange,
  loading = false,
  maxThreads = 5,
  readOnly = false
}) => {
  const isMobile = useIsMobile("sm");
  const user = useUser();
  const { t } = useTranslation();
  
  // threads를 직접 사용 (controlled component)
  const currentThreads = threads;
  
  // UserContext에서 사용자 정보 추출 (Header.js와 동일한 방식)
  const userProfile = {
    threads_username: user?.profile?.username || localStorage.getItem("username") || "사용자",
    threads_profile_picture_url: localStorage.getItem("threads_profile_picture_url") || user?.profile?.threads_profile_picture_url || user?.profile?.profile_picture_url || null
  };



  // 스레드 텍스트 변경
  const handleTextChange = (threadId, text) => {
    if (readOnly || !onThreadsChange) return; // readOnly 모드에서는 변경 불가
    
    const updatedThreads = threads.map(thread => 
      thread.id === threadId 
        ? { ...thread, text }
        : thread
    );
    
    // 부모 컴포넌트에 변경사항 전달
    onThreadsChange(updatedThreads);
  };

  // 새 스레드 추가
  const addThread = () => {
    if (readOnly || currentThreads.length >= maxThreads || !onThreadsChange) return;
    
    const newThread = {
      id: Date.now(),
      text: "",
      isMain: false
    };
    onThreadsChange([...threads, newThread]);
  };

  // 스레드 삭제
  const removeThread = (threadId) => {
    if (readOnly || !onThreadsChange) return; // readOnly 모드에서는 삭제 불가
    
    onThreadsChange(
      threads.filter(thread => thread.id !== threadId)
    );
  };


  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#fff",
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(2),
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
      }}
    >
      <Stack spacing={theme.spacing(2)}>
        {/* 스레드 입력들 */}
        {currentThreads.map((thread, index) => (
          <React.Fragment key={thread.id}>
            <ThreadInput
              thread={thread}
              onTextChange={handleTextChange}
              onRemove={removeThread}
              loading={loading}
              userProfile={userProfile}
              showRemoveButton={currentThreads.length > 1 && !readOnly}
              placeholder={thread.isMain ? t("postThreads.composer.placeholderMain") : t("postThreads.composer.placeholderReply")}
              isLast={index === currentThreads.length - 1}
              readOnly={readOnly}
            />
          </React.Fragment>
        ))}

        {/* Add to thread 버튼 - readOnly 모드에서는 숨김 */}
        {!readOnly && currentThreads.length < maxThreads && (
          <Box 
            sx={{ 
              display: "flex",
              gap: theme.spacing(1.5),
              alignItems: "center",
              marginTop: theme.spacing(1)
            }}
          >
            {/* 작은 프로필 이미지 */}
            <Box sx={{ flexShrink: 0 }}>
              <Avatar
                src={userProfile?.threads_profile_picture_url}
                alt={userProfile?.threads_username || "User"}
                sx={{ width: 24, height: 24 }}
              />
            </Box>

            {/* Add to thread 버튼 */}
            <Button
              variant="text"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={addThread}
              disabled={loading}
              sx={{
                color: "#8e8e8e",
                backgroundColor: "transparent",
                border: "none",
                padding: 0,
                height: "auto",
                fontSize: "14px",
                textTransform: "none",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#666"
                }
              }}
            >
              {t("postThreads.composer.addToThread")}
            </Button>
          </Box>
        )}

        {/* 스레드 수 제한 안내 - readOnly 모드에서는 숨김 */}
        {!readOnly && currentThreads.length >= maxThreads && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.text.secondary,
              textAlign: "center"
            }}
          >
            {t("postThreads.composer.maxThreadsLimit", { maxThreads })}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ThreadComposer; 